import React, { useState } from "react";
import styled from "@emotion/styled";
import { SmallText } from "../../../../components/core/Utility";
import { Tooltip } from "primereact/tooltip";
import InfoIcon from "../../../assets/icons/info_white.png";
import ErrorImg from "../../../assets/icons/Error.svg";
import ReleasedImg from "../../../assets/icons/Released.svg";
import ShippedImg from "../../../assets/icons/Shipped.png";
import CancelImg from "../../../assets/icons/Cancelled.svg";
import BackorderedImg from "../../../assets/icons/Backordered.svg";
import ReturnSubmittedImg from "../../../assets/icons/ReturnSubmitted.svg";
import SubmittedImg from "../../../assets/icons/Submitted.svg";
import CreatedImg from "../../../assets/icons/Created.svg";
import PackagingImg from "../../../assets/icons/Packaging.svg";
import DeliveredImg from "../../../assets/icons/Delivered.svg";
import onGoCarIcon from "../../../assets/icons/onGoCar.png";
import adaptiveSpeaker from "../../../assets/icons/adaptive_speaker.svg";
import keyfob from "../../../assets/icons/keyfob.png";
import { Icon } from "@vds/icons";
import "primeicons/primeicons.css";
import DoNotShipImg from "../../../assets/icons/DoNotShip.svg";
import { DEVICE_TYPES } from "./AssignHardwareConstants";

const rowsDefault = [{ row: "" }];

// Bulk select row container
const BulkSelectRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #fff;
  border: 1px solid #dddad4;
  border-bottom: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const BulkSelectCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BulkSelectLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #000;
`;

const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #000;
  border-radius: 3px;
  background-color: #fff;
  position: relative;
  outline: none;

  &:checked {
    background-color: #000;
    border-color: #000;
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:hover {
    border-color: #000;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.2);
  }
`;

// Main container for each vehicle card
const VehicleCard = styled.div`
  border: 1px solid #dddad4;
  overflow: hidden;
  font-family: Verizon-NHG-eTX, Helvetica, Arial, sans-serif;

  /* Remove gap between cards by using negative margin to overlap borders */
  &:not(:first-of-type) {
    margin-top: -1px;
  }

  /* Only apply border-radius to first and last cards */
  &:first-of-type {
    border-top-left-radius: ${(props) => (props.hasBulkSelect ? "0" : "8px")};
    border-top-right-radius: ${(props) => (props.hasBulkSelect ? "0" : "8px")};
  }

  &:last-of-type {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

// Top header section with vehicle info
const VehicleHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #aaa8a31f;
  border-bottom: 1px solid #dddad4;
`;

const CheckboxContainer = styled.div`
  margin-right: 12px;
`;

const VehicleInfo = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.1fr 1.7fr 2fr 0.8fr;
  gap: 16px;
  align-items: center;
`;

const VehicleInfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const VehicleInfoLabel = styled.span`
  font-size: 12px;
  color: #000;
  font-weight: 700;
  margin-bottom: 4px;
`;

const VehicleInfoValue = styled.span`
  font-size: 12px;
  color: ${(props) => props.textColor || "#000"};
  font-weight: ${(props) => (props.bold ? "700" : "400")};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  width: max-content;
  background-color: ${(props) => {
    switch (props.status) {
      case "Ready":
        return "#008331";
      case "Warning":
        return "#FFCD27";
      default:
        return "#9E9E9E";
    }
  }};
  color: ${(props) => (props.status === "Warning" ? "#000" : "#fff")};
`;

const PriorityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #000;
`;

const TraitsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

// Subscription table section
const SubscriptionTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    padding: 10px 16px;
    font-weight: 600;
    color: #000;
    border-bottom: 1px solid #dddad4;
    border-right: 1px solid #dddad4;
    background-color: #fff;

    &:last-child {
      border-right: none;
    }
  }

  td {
    padding: 10px 16px;
    vertical-align: top;
    border-right: 1px solid #dddad4;
    color: ${(props) => props.textColor || "#000"};

    &:last-child {
      border-right: none;
    }
  }

  tr.group-last td {
    border-bottom: 1px solid #dddad4;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const HardwareCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WorkTicketCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
`;

const IconWrapper = styled.div`
  height: 18px;
  width: 18px;
  align-items: center;
  cursor: pointer;
  display: flex;
  margin-left: 2px;
  margin-bottom: 0;
  padding: 0;
`;

const TextWrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`;

export const NewAssignHardwareGrid = ({
  noDataMessage = "No data to display",
  rows = { rowsDefault },
  editVehicle,
  isVehicleEditable,
  bundleName,
  bundleId,
  shipGroupsData,
  assetType,
  isBulkAction = false,
  isBulkActionSelected = false,
  handleBulkVehicleSelection,
  installTypeMap,
  renderActionIcon,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  const { addressId, contactId } = shipGroupsData || {};

  const isClosedLineItem = (materialObj) => {
    return materialObj.status?.toLowerCase() === "submitted";
  };

  // Define sort order for types
  const getTypeOrder = (type) => {
    const typeUpper = type?.toUpperCase() || "";
    switch (typeUpper) {
      case "CORE":
        return 1;
      case "COMPANION":
        return 2;
      case "FEATURE":
        return 3;
      default:
        return 4;
    }
  };

  // Define sort order for subtypes
  const getSubTypeOrder = (subType) => {
    const subTypeUpper = subType?.toUpperCase() || "";
    switch (subTypeUpper) {
      case "MAIN":
        return 1;
      case "ACCESSORY":
        return 2;
      default:
        return 3;
    }
  };

  const sortMaterials = (materials) => {
    if (!materials || materials.length === 0) return [];

    return [...materials].sort((a, b) => {
      // First, sort by type
      const typeOrderA = getTypeOrder(a.type);
      const typeOrderB = getTypeOrder(b.type);

      if (typeOrderA !== typeOrderB) {
        return typeOrderA - typeOrderB;
      }

      // If types are the same, sort by subtype
      const subTypeOrderA = getSubTypeOrder(a.subType);
      const subTypeOrderB = getSubTypeOrder(b.subType);

      return subTypeOrderA - subTypeOrderB;
    });
  };

  const groupMaterialsBySubscription = (materials) => {
    if (!materials || materials.length === 0) return [];

    // First, sort the materials
    const sortedMaterials = sortMaterials(materials);

    const grouped = sortedMaterials.reduce((acc, material) => {
      const subId = material.subscriptionId || "unknown";
      if (!acc[subId]) {
        acc[subId] = {
          subscriptionId: material.subscriptionId,
          subscriptionName: material.subscriptionName,
          techRefId: material.techRefId,
          materials: [],
        };
      }
      acc[subId].materials.push(material);
      return acc;
    }, {});

    // Convert to array and sort each group's materials
    const groupedArray = Object.values(grouped);

    // Sort materials within each group as well (in case grouping changed order)
    groupedArray.forEach((group) => {
      group.materials = sortMaterials(group.materials);
    });

    // Sort groups so CORE type subscriptions come first
    groupedArray.sort((a, b) => {
      const aFirstType = getTypeOrder(a.materials[0]?.type);
      const bFirstType = getTypeOrder(b.materials[0]?.type);
      return aFirstType - bFirstType;
    });

    return groupedArray;
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedVehicles([...rows]);
      handleBulkVehicleSelection([...rows], `${addressId}-${contactId}`);
    } else {
      setSelectedVehicles([]);
      handleBulkVehicleSelection([], `${addressId}-${contactId}`);
    }
  };

  const handleSelectVehicle = (vehicle, e) => {
    const isChecked = e.target.checked;
    const vehicleId = vehicle.id || vehicle.vehicle;
    if (isChecked) {
      const newSelected = [...selectedVehicles, vehicle];
      setSelectedVehicles(newSelected);
      // Check if all vehicles are now selected
      if (newSelected.length === rows.length) {
        setSelectAll(true);
      }
      handleBulkVehicleSelection(newSelected, `${addressId}-${contactId}`);
    } else {
      const newSelected = selectedVehicles.filter((v) => (v.id || v.vehicle) !== vehicleId);
      setSelectedVehicles(newSelected);
      setSelectAll(false);
      handleBulkVehicleSelection(newSelected, `${addressId}-${contactId}`);
    }
  };

  const isVehicleSelected = (vehicleId) => {
    return selectedVehicles.some((v) => (v.id || v.vehicle) === vehicleId);
  };

  const renderTraitsIcons = (row) => {
    const hasTraits = row.weightClass || row.wheelChair || row.emergency || row.killSwitch;

    if (!hasTraits) {
      return <span style={{ fontSize: "12px", color: "#747676" }}>--</span>;
    }
    return (
      <TraitsContainer>
        {row.weightClass && <span style={{ fontSize: "12px" }}>{row.weightClass}</span>}
        {row.wheelChair && <Icon name="international-symbol-of-access" size="medium" color="#000" style={{ width: "18px", height: "18px" }} />}
        {row.emergency && <Icon name="police-fleet" size="medium" color="#000" style={{ width: "18px", height: "18px" }} />}
        {row.killSwitch && <Icon name="smart-switch" size="medium" color="#000" style={{ width: "18px", height: "18px" }} />}
      </TraitsContainer>
    );
  };

  const renderPriorityBadge = (row) => {
    const priority = Number(row.priority);
    const tooltipText = priority === 1 ? "Hardware Recommended" : "Hardware Specified";
    return (
      <PriorityBadge>
        <span className={`priority-${row.id}`} data-pr-tooltip={tooltipText}>
          Priority {priority}
        </span>
      </PriorityBadge>
    );
  };

  const renderVehicleStatus = (row) => {
    return (
      <div>
        {/* Warning status with comment */}
        {row.vehicleStatus === "Warning" && row?.comment?.length > 0 && (
          <StyledIconContainer>
            <StatusBadge status="Warning">
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                Needs Attention
                <img
                  alt="Error icon"
                  className={`errorlogo-${row.id}`}
                  src={InfoIcon}
                  height="14px"
                  width="14px"
                  style={{
                    verticalAlign: "middle",
                  }}
                  aria-label="Error"
                  data-pr-tooltip={row?.comment}
                />
              </span>
            </StatusBadge>
            <Tooltip target={`.errorlogo-${row.id}`} mouseTrack mouseTrackLeft={10} />
          </StyledIconContainer>
        )}

        {/* Warning status without comment */}
        {row.vehicleStatus === "Warning" && row?.comment == null && (
          <StyledIconContainer>
            <StatusBadge status="Warning">Needs Attention</StatusBadge>
          </StyledIconContainer>
        )}

        {/* Ready status with comment */}
        {row.vehicleStatus === "Ready" && row?.comment?.length > 0 && (
          <StyledIconContainer>
            <TextWrapper>
              {/* <Tooltip target={`.info-${row.id}`} mouseTrack mouseTrackLeft={10} /> */}
              {/* verify if hover over status is it required as weare showing priority */}
              <StatusBadge
                status="Ready"
                // data-pr-tooltip={Number(row.priority) === 1 ? "Hardware Recommended" : "Hardware Specified"}
                className={`info-${row.id}`}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  Ready
                  <img
                    alt="Error icon"
                    className={`errorlogo-${row.id}`}
                    src={InfoIcon}
                    height="14px"
                    width="14px"
                    style={{
                      visibility: row?.comment?.length > 0 ? "visible" : "hidden",
                      verticalAlign: "middle",
                    }}
                    aria-label="Error"
                    data-pr-tooltip={row?.comment}
                  />
                </span>
              </StatusBadge>
              <Tooltip target={`.errorlogo-${row.id}`} mouseTrack mouseTrackLeft={10} />
            </TextWrapper>
          </StyledIconContainer>
        )}

        {/* Ready status without comment */}
        {row.vehicleStatus === "Ready" && row?.comment == null && (
          <StyledIconContainer>
            <TextWrapper>
              {/* <Tooltip target={`.info-${row.id}`} mouseTrack mouseTrackLeft={10} /> */}
              <StatusBadge
                status="Ready"
                // data-pr-tooltip={Number(row.priority) === 1 ? "Hardware Recommended" : "Hardware Specified"}
                className={`info-${row.id}`}
              >
                Ready
              </StatusBadge>
            </TextWrapper>
          </StyledIconContainer>
        )}
      </div>
    );
  };

  const renderSubscriptionCell = (group, isFirstRow, isLastGroup) => {
    if (!isFirstRow) return null;

    return (
      <td
        rowSpan={group.materials.length}
        style={{
          color: "#000",
          verticalAlign: "top",
          borderRight: "1px solid #dddad4",
          borderBottom: isLastGroup ? "none" : "1px solid #dddad4",
        }}
      >
        <div style={{ fontSize: "12px" }}>
          <div>{group.subscriptionName || "--"}</div>
          <div style={{ color: "#716F6D" }}>({group.subscriptionId || "--"})</div>
          <div>
            <b>Tech-Ref ID:</b> {group.techRefId || "--"}
          </div>
          {/* Once the new key is introduced in vehicle details api use them */}
          <div>
            <b>Install Type:</b> {installTypeMap[group.subscriptionId] || "--"}
          </div>
        </div>
      </td>
    );
  };

  if (!rows?.length) {
    return <SmallText>{noDataMessage}</SmallText>;
  }

  const renderVehicleTitle = (vehicle) => {
    if (vehicle.deviceType === DEVICE_TYPES.KEYFOB) {
      return "KeyFob";
    } else if (vehicle.deviceType === DEVICE_TYPES.VEHICLE) {
      return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    } else if (vehicle.deviceType === DEVICE_TYPES.ASSET) {
      return vehicle?.materialMapping[0]?.subscriptionName.startsWith("Non") ? "Non Powered Asset" : "Powered Asset";
    }
  };

  const cleanSourceApplication = (sourceApplication) => {
    if (!sourceApplication) return "--";
    // Remove "OPT" or "OPT-" or "OPT " prefix (case-insensitive)
    return sourceApplication.replace(/^OPT[-\s]*/i, "").trim() || "--";
  };

  return (
    <div>
      {/* Bulk Select Row - Only shown when showCheckbox is true */}
      {isBulkActionSelected && (
        <BulkSelectRow>
          <BulkSelectCheckbox>
            <StyledCheckbox type="checkbox" checked={selectAll} onChange={handleSelectAll} />
            <BulkSelectLabel>Vehicle/Asset</BulkSelectLabel>
          </BulkSelectCheckbox>
        </BulkSelectRow>
      )}

      {rows.map((vehicle, vehicleIndex) => {
        const isEditable = isVehicleEditable(vehicle) && !isBulkAction;
        const vehicleId = vehicle.id || vehicle.vehicle;
        const groupedMaterials = groupMaterialsBySubscription(vehicle.materialMapping);
        const isVehicle = vehicle.deviceType === DEVICE_TYPES.VEHICLE;
        const isAsset = vehicle.deviceType === DEVICE_TYPES.ASSET;
        const isKeyfob = vehicle.deviceType === DEVICE_TYPES.KEYFOB;

        return (
          <VehicleCard key={`vehicle-${vehicleId || vehicleIndex}`} hasBulkSelect={isBulkActionSelected}>
            {/* Vehicle Header Section */}
            <VehicleHeader>
              {isBulkActionSelected && (
                <CheckboxContainer>
                  <StyledCheckbox type="checkbox" checked={isVehicleSelected(vehicleId)} onChange={(e) => handleSelectVehicle(vehicle, e)} />
                </CheckboxContainer>
              )}

              <VehicleInfo>
                {/* Vehicle Name & VIN */}
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div>
                    <img
                      src={isVehicle ? onGoCarIcon : isAsset ? adaptiveSpeaker : keyfob}
                      alt={isVehicle ? "Vehicle Icon" : isAsset ? "adaptive-speaker-icon" : keyfob}
                      height={isKeyfob ? "15px" : "25px"}
                      width="25px"
                      style={{ marginRight: "6px" }}
                    />
                  </div>
                  <VehicleInfoItem>
                    <VehicleInfoValue
                      bold
                      style={{
                        textDecoration: isEditable ? "underline" : "none",
                        cursor: isEditable ? "pointer" : "not-allowed",
                      }}
                      onClick={() => {
                        if (isEditable) {
                          editVehicle(vehicle, bundleName, bundleId, shipGroupsData, assetType);
                        }
                      }}
                    >
                      {renderVehicleTitle(vehicle)}
                    </VehicleInfoValue>
                    {vehicle.displayVinId && <VehicleInfoValue style={{ fontSize: "11px", color: "#333332" }}>{vehicle.vehicle}</VehicleInfoValue>}
                  </VehicleInfoItem>
                </div>

                {/* Hardware Status */}
                <VehicleInfoItem>
                  <VehicleInfoLabel>Hardware Status</VehicleInfoLabel>
                  {renderVehicleStatus(vehicle)}
                  {vehicle.isShippingBlocked && <VehicleInfoValue style={{ paddingTop: "4px" }}>Hold Vehicle</VehicleInfoValue>}
                </VehicleInfoItem>

                {/* Hardware Priority */}
                <VehicleInfoItem>
                  <VehicleInfoLabel>Hardware</VehicleInfoLabel>
                  {renderPriorityBadge(vehicle)}
                </VehicleInfoItem>

                {/* Traits */}
                <VehicleInfoItem>
                  <VehicleInfoLabel>Traits</VehicleInfoLabel>
                  {renderTraitsIcons(vehicle)}
                </VehicleInfoItem>

                {/* Installation Address */}
                <VehicleInfoItem>
                  <VehicleInfoLabel>Installation Address</VehicleInfoLabel>
                  <VehicleInfoValue style={{ fontSize: "11px" }}>Same as shipping address</VehicleInfoValue>
                </VehicleInfoItem>

                {/* OPT */}
                <VehicleInfoItem>
                  <VehicleInfoLabel>OPT</VehicleInfoLabel>
                  <VehicleInfoValue textColor="#716F6D">{cleanSourceApplication(vehicle.sourceApplication)}</VehicleInfoValue>
                </VehicleInfoItem>
              </VehicleInfo>

              {/* Action Icons */}
              <ActionContainer>{renderActionIcon(vehicle)}</ActionContainer>
            </VehicleHeader>

            {/* Subscription Table Section */}
            {vehicle.materialMapping && vehicle.materialMapping.length > 0 && (
              <SubscriptionTable>
                <thead>
                  <tr>
                    <th style={{ width: isKeyfob ? "23%" : "30%" }}>Subscription</th>
                    {isKeyfob ? <th style={{ width: "7%" }}>Quantity</th> : <></>}
                    <th style={{ width: "14%" }}>Category</th>
                    <th style={{ width: "21%" }}>Hardware</th>
                    <th style={{ width: "12%" }}>Type</th>
                    <th style={{ width: "12%" }}>Work Ticket</th>
                    <th style={{ width: "12%" }}>Delivery Date</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedMaterials.map((group, groupIndex) =>
                    group.materials.map((material, materialIndex) => {
                      const textColor = isClosedLineItem(material) ? "#999" : "#000";
                      const isFirstRowInGroup = materialIndex === 0;
                      const isLastRowInGroup = materialIndex === group.materials.length - 1;
                      const isLastGroup = groupIndex === groupedMaterials.length - 1;
                      const showWorkTicket = !(
                        (material.status === "Ready" || material.status === "Warning") &&
                        material.wtstatus?.toLowerCase() !== "cancelled"
                      );

                      return (
                        <tr key={`material-${groupIndex}-${materialIndex}`} className={isLastRowInGroup && !isLastGroup ? "group-last" : ""}>
                          {/* Subscription */}
                          {renderSubscriptionCell(group, isFirstRowInGroup, isLastGroup)}

                          {/* Quantity - Only for KeyFob */}
                          {isKeyfob && <td style={{ color: textColor }}>{material.quantity || "--"}</td>}

                          {/* Category */}
                          <td style={{ color: textColor }}>
                            {material.type || "--"} - {material.subType || "--"}
                          </td>

                          {/* Hardware */}
                          <td style={{ color: textColor }}>
                            <HardwareCell>
                              <span>
                                {material.hardwareName || "--"}
                                {material.hardwareId && (
                                  <>
                                    <br />
                                    {material.hardwareId}
                                    {material.included && " (Included)"}
                                  </>
                                )}
                              </span>
                              {material?.isShipmentHold && (
                                <IconWrapper>
                                  <Tooltip target={`.shiphold-${materialIndex}`} mouseTrack mouseTrackLeft={10} />
                                  <img
                                    alt="Do Not Ship"
                                    className={`shiphold-${materialIndex}`}
                                    src={DoNotShipImg}
                                    height="16px"
                                    width="16px"
                                    data-pr-tooltip="Do Not Ship"
                                  />
                                </IconWrapper>
                              )}
                            </HardwareCell>
                          </td>

                          {/* Type */}
                          <td style={{ color: textColor }}>{material.lineType || "--"}</td>

                          {/* Work Ticket */}
                          <td style={{ color: textColor }}>
                            <WorkTicketCell>
                              <span>{showWorkTicket ? material.workTicketId || "--" : "--"}</span>
                              {showWorkTicket && renderImage(material) && (
                                <IconWrapper>
                                  <Tooltip target={`.wt-${materialIndex}`} mouseTrack mouseTrackLeft={10} />
                                  <img
                                    alt="Status"
                                    className={`wt-${materialIndex}`}
                                    src={renderImage(material)}
                                    height="16px"
                                    width="16px"
                                    data-pr-tooltip={material?.wtstatus || ""}
                                  />
                                </IconWrapper>
                              )}
                            </WorkTicketCell>
                          </td>

                          {/* Delivery Date */}
                          <td style={{ color: textColor }}>{material.deliveryDate || "--"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </SubscriptionTable>
            )}
          </VehicleCard>
        );
      })}
    </div>
  );
};

export const renderImage = (data) => {
  let workTicketStatus = data?.wtstatus ? data?.wtstatus?.toLowerCase() : "";
  let statusIcon;
  switch (workTicketStatus) {
    case "error":
      statusIcon = ErrorImg;
      break;
    case "released":
      statusIcon = ReleasedImg;
      break;
    case "shipped":
      statusIcon = ShippedImg;
      break;
    case "cancelled":
      statusIcon = CancelImg;
      break;
    case "return submitted":
      statusIcon = ReturnSubmittedImg;
      break;
    case "backordered":
      statusIcon = BackorderedImg;
      break;
    case "submitted":
      statusIcon = SubmittedImg;
      break;
    case "created":
      statusIcon = CreatedImg;
      break;
    case "packaging":
      statusIcon = PackagingImg;
      break;
    case "delivered":
      statusIcon = DeliveredImg;
      break;
    default:
      statusIcon = null;
  }
  return statusIcon;
};
